import {ChipOptions} from '@entities/common/common.interface';

export namespace UniversalTable {
	export enum ColumnType {
		String,
		Number,
		Pattern,
		Chips,
		Date,
	}

	export type CellValueFormatterFunction = (v?: number | string) => string | number;

	export enum StyleName {
		Display = 'display',
		GridTemplateColumn = 'grid-template-columns',
	}

	/**
	 * Auxiliary StylesConfig interface declaration.
	 */
	export interface StylesConfig {
		/** {string} [StyleName.Display] */
		[StyleName.Display]?: string;
		/** {string} [StyleName.GridTemplateColumn] */
		[StyleName.GridTemplateColumn]?: string;
	}

	/**
	 * Auxiliary BasicColumn interface declaration.
	 */
	export interface BasicColumn {
		/** {ColumnType} type */
		type: ColumnType;
		/** {string} title */
		title?: string;
		/** {string} width */
		width?: string;
		/** {number} widthFr */
		widthFr?: number;
		/** {string} id */
		id?: string;
		/** {boolean} editable */
		editable?: boolean;
		/** {string} required */
		required?: string;
		/** {boolean} sortable */
		sortable?: boolean;
		/** {boolean} draggable */
		draggable?: boolean;
		/** {string} sortField */
		sortField?: string;
		/** {boolean} enableHtmlTooltip */
		enableHtmlTooltip?: boolean;
		/** {boolean} withSearch */
		withSearch?: boolean;
		/** {boolean} withDate */
		withDate?: boolean;
		/** {boolean} filterable */
		filterable?: boolean;
	}

	/**
	 * Auxiliary TextColumn interface declaration.
	 */
	export interface TextColumn extends BasicColumn {
		/** {ColumnType.String | ColumnType.Number} type */
		type: ColumnType.String | ColumnType.Number;
		/** {string} textClass */
		textClass?: string;
		/** {CellValueFormatterFunction} formatter */
		formatter?: CellValueFormatterFunction;
	}

	/**
	 * Auxiliary DateColumn interface declaration.
	 */
	export interface DateColumn extends BasicColumn {
		/** {ColumnType.Date} type */
		type: ColumnType.Date;
		/** {string} textClass */
		textClass?: string;
		/** {CellValueFormatterFunction} formatter */
		formatter?: CellValueFormatterFunction;
	}

	/**
	 * Auxiliary PatternColumn interface declaration.
	 */
	export interface PatternColumn<T> extends BasicColumn {
		/** {ColumnType.Pattern} type */
		type: ColumnType.Pattern;
		/** {T} patternType */
		patternType: T;
		/** {string} icon */
		icon?: string;
		/** {string} label */
		label?: string;
	}

	/**
	 * Auxiliary ChipOptionsMap interface declaration.
	 */
	export interface ChipOptionsMap {
		/** {ChipOptions} key as string */
		[key: string]: ChipOptions;
	}

	/**
	 * Auxiliary ChipsColumn interface declaration.
	 */
	export interface ChipsColumn extends BasicColumn {
		/** {ColumnType.Chips} type */
		type: ColumnType.Chips;
		/** {ChipOptionsMap} optionsMap */
		optionsMap: ChipOptionsMap;
		/** separator */
		separator: {
			iconKey: string;
		};
	}

	export type ColumnOptions<T> = PatternColumn<T> | ChipsColumn | TextColumn | BasicColumn;

	/**
	 * Auxiliary RowsOptions interface declaration.
	 */
	export interface RowsOptions {
		/** {boolean} isClickable */
		isClickable: boolean;
		/** {boolean} isStriped */
		isStriped: boolean;
	}

	/**
	 * Auxiliary Options interface declaration.
	 */
	export interface Options {
		/** {RowsOptions} row */
		row: RowsOptions;
	}

	export type Row<T> = T;

	/**
	 * Auxiliary Model interface declaration.
	 */
	export interface Model<T1, T2> {
		/** {Row<T1>[]} rows */
		rows: Row<T1>[];
		/** {ColumnOptions<T2>[]} columns */
		columns: ColumnOptions<T2>[];
		/** {Options} options */
		options: Options;
	}

	export enum SortingOrder {
		Asc = 'ASC',
		Desc = 'DESC',
	}

	/**
	 * Auxiliary SortingColumn interface declaration.
	 */
	export interface SortingColumn {
		/** {string} id */
		id: string;
		/** {string} sortField */
		sortField?: string;
	}

	/**
	 * Auxiliary Sorting interface declaration.
	 */
	export interface Sorting {
		/** {SortingColumn} column */
		column: SortingColumn;
		/** {SortingOrder} order */
		order: SortingOrder;
	}
}
